import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useRive,
  useViewModel,
  useViewModelInstance,
  useViewModelInstanceTrigger,
  EventType
} from '@rive-app/react-canvas';
import { getTaxFullUrl } from '../utils';
import stepCompleteIcon from './icons/icon-step-complete.svg';

const STATE_MACHINE = 'State Machine 1';

// Data-binding trigger the state machine fires when the step animation reaches
// its end. Rive deprecated state-change and Rive Event listeners in favor of
// view model property observers, so the graphic must expose this trigger on its
// default view model for the step to advance on the animation's own timing.
const COMPLETE_TRIGGER = 'onComplete';

// Rive renders on requestAnimationFrame, which browsers pause in background
// tabs (and Rive deliberately doesn't catch up on the time missed), so the
// animation can't signal completion while the tab is hidden. Timers keep
// firing, so while a step is active this checks on that interval whether the
// tab is hidden and, if so, advances the step without waiting on the animation.
const STEP_FALLBACK_MS = 3000;

// __webpack_public_path__ is absolute in local dev (the webpack-dev-server root),
// but root-relative in a deployed build, resolving against the current document's
// origin. That's fine when this page is served from the same origin as the bundle,
// but breaks now that the results page can load inside Magnolia on a different host.
function getRiveSrc(): string {
  return /^https?:\/\//.test(__webpack_public_path__)
    ? `${__webpack_public_path__}tax_results_loading.riv`
    : `${getTaxFullUrl()}/assets/dist/tax_results_loading.riv`;
}

function StepSpinner({
  isActive,
  onComplete
}: {
  readonly isActive: boolean;
  readonly onComplete: () => void;
}) {
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const { RiveComponent, rive } = useRive({
    src: getRiveSrc(),
    stateMachine: STATE_MACHINE,
    autoplay: false
  });

  // Doubles as the "this step is running" gate and the fire-once guard, so a
  // trigger or stop event can only complete the step while it is active.
  const runningRef = useRef(false);
  // Rive can't be fast-forwarded to its end frame, so a step the fallback
  // finished in a hidden tab shows a static checkmark instead of a canvas
  // that would otherwise replay or sit frozen mid-animation on return.
  const [finishedWhileHidden, setFinishedWhileHidden] = useState(false);
  const complete = useCallback(() => {
    if (!runningRef.current) return;
    runningRef.current = false;
    onCompleteRef.current();
  }, []);

  const viewModel = useViewModel(rive, { useDefault: true });
  const viewModelInstance = useViewModelInstance(viewModel, { rive });
  useViewModelInstanceTrigger(COMPLETE_TRIGGER, viewModelInstance, {
    onTrigger: complete
  });

  // Independent of `rive` so a step still advances if its animation instance
  // isn't ready (or can't run) when the step becomes active.
  useEffect(() => {
    if (!isActive) return;
    runningRef.current = true;
    let fallbackTimer: ReturnType<typeof setTimeout>;
    // Only acts while the tab is hidden: a visible tab means Rive is rendering
    // and will finish on its own, and completing early would cut it off partway
    // through (it only advances while visible, wall-clock time doesn't).
    const scheduleFallback = () => {
      fallbackTimer = setTimeout(() => {
        if (!document.hidden) {
          scheduleFallback();
          return;
        }
        setFinishedWhileHidden(true);
        complete();
      }, STEP_FALLBACK_MS);
    };
    scheduleFallback();
    return () => {
      clearTimeout(fallbackTimer);
      runningRef.current = false;
    };
  }, [isActive, complete]);

  useEffect(() => {
    if (!rive || !isActive) return;
    rive.play(STATE_MACHINE);

    // Fallback for graphics that end by stopping playback rather than firing
    // the completion trigger; without it the step would never advance.
    rive.on(EventType.Stop, complete);

    return () => {
      rive.off(EventType.Stop, complete);
    };
  }, [rive, isActive, complete]);

  // The canvas stays mounted (just hidden) even once the checkmark shows:
  // unmounting it frees the Rive instance, and the view-model hooks above would
  // then read freed wasm memory on their next render and crash.
  return (
    <div className="relative w-full h-full">
      <RiveComponent
        className={finishedWhileHidden ? 'invisible' : undefined}
      />
      {finishedWhileHidden && (
        <img
          src={stepCompleteIcon}
          alt=""
          aria-hidden="true"
          data-testid="step_complete_icon"
          className="absolute inset-0 w-full h-full"
        />
      )}
    </div>
  );
}

export { StepSpinner };
